import {
    type AccountInfo,
    AuthError,
    BrowserAuthError,
    InteractionRequiredAuthError,
    type IPublicClientApplication,
    type SilentRequest,
} from "@azure/msal-browser";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import { scopesNeeded } from "./authConfig";

interface FetchParams {
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    payload?: Record<string, unknown> | Record<string, unknown>[];
    instance: IPublicClientApplication;
    expectedStatus?: number;
}

type isForbiddenGetRequestInput = {
    response: Response;
    options: RequestInit;
    endpoint: string;
};

type ResponseHandlerInput = {
    response: Response;
    options: RequestInit;
    url?: string;
    expectedStatus?: number;
};

interface CustomWindow extends Window {
    __DEBUG_FORCE_INTERACTION_REQUIRED?: boolean;
}
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
export const DEBUG_FORCE_INTERACTION_REQUIRED = () =>
    import.meta.env.VITE_ENVIRONMENT === "dev" &&
    (window as CustomWindow).__DEBUG_FORCE_INTERACTION_REQUIRED === true;
// Simple health check cache
let healthCheckCache = {
    isHealthy: true,
    timestamp: 0,
};

const CACHE_DURATION = 30000; // 30 seconds cache for health check results (should be enough until the user logs in to Zscaler)
export const FORBIDDEN_KEY = "isForbidden";

const setForbiddenState = (isForbidden = true): void => {
    localStorage.setItem(FORBIDDEN_KEY, isForbidden.toString());
};

const createHeaders = (token?: string) => {
    const transactionId = uuidv4();

    Sentry.withScope((scope) => {
        scope.setTag("transaction_id", transactionId);
    });
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Transaction-ID": transactionId,
    };
};

const isForbiddenGetRequest = ({
    response,
    options,
    endpoint,
}: isForbiddenGetRequestInput): boolean =>
    response.status === 403 && options.method === "GET" && endpoint.includes("/v1/user");

export const handleResponse = async ({
    response,
    options,
    url,
    expectedStatus,
}: ResponseHandlerInput): Promise<Response> => {
    const endpoint = url ? new URL(url).pathname : "";

    if (!response.ok) {
        if (isForbiddenGetRequest({ response, options, endpoint })) {
            setForbiddenState(true);
        }

        throw new Error(`Request to ${endpoint} failed with status ${response.status}`);
    }

    if (expectedStatus !== undefined && response.status !== expectedStatus) {
        throw new Error(`Request to ${endpoint} returned unexpected status ${response.status}`);
    }

    return response;
};

export const postAPI = async ({
    url,
    payload,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "POST",
            body: JSON.stringify(payload),
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

/**
 * Acquires an authentication token silently, with two safeguards for embedded
 * (iframe) usage such as the widget / copilot:
 *
 *   1. Single-flight: concurrent callers share one in-flight acquisition so only
 *      one interactive (popup) request is ever started. Firing several popups at
 *      once is what triggers `BrowserAuthError: interaction_in_progress`.
 *   2. interaction_in_progress recovery: when another flow (e.g. the widget auth
 *      provider, or another tab) already holds MSAL's global interaction lock,
 *      wait for it to clear and retry silently instead of opening a competing
 *      popup — its result is usually in the token cache by then.
 *
 * @param instance The MSAL instance to use for token acquisition
 * @returns The acquired token, or null when interaction must be handled elsewhere
 */
type AcquireAuthTokenOptions = {
    forceRefresh?: boolean;
};

const INTERACTION_RETRY_DELAY_MS = 500;
const INTERACTION_MAX_RETRIES = 10;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const isInteractionInProgressError = (error: unknown): boolean =>
    error instanceof BrowserAuthError &&
    (error.errorCode === "interaction_in_progress" ||
        error.message.includes("interaction_in_progress"));

// Shared in-flight acquisitions, keyed by whether a forced refresh was requested.
// A forced refresh must run its own request; a silent caller can piggyback on an
// in-flight silent OR forced request.
const inFlightTokenRequests: Record<"silent" | "force", Promise<string | null> | null> = {
    silent: null,
    force: null,
};

const acquireTokenInteractive = async (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
): Promise<string | null> => {
    const isInIframe = window.self !== window.top;
    if (!isInIframe) {
        // In non-iframe context, MsalAuthenticationTemplate handles the redirect.
        // Calling acquireTokenRedirect here would race with it and cause interaction_in_progress.
        return null;
    }

    try {
        const response = await instance.acquireTokenPopup({
            scopes: scopesNeeded,
            account,
        });
        return response.accessToken;
    } catch (interactiveError) {
        // A popup that races another interaction throws interaction_in_progress.
        // Surface a soft error so callers don't spin on `Bearer null` -> 401 retries.
        if (isInteractionInProgressError(interactiveError)) {
            throw new Error("Authentication in progress, please wait");
        }
        console.error("Error acquiring token interactively:", interactiveError);
        return null;
    }
};

const acquireAuthTokenInner = async (
    instance: IPublicClientApplication,
    forceRefresh: boolean,
): Promise<string | null> => {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0] || null;

    // Use proper scopes to enable MSAL caching - empty scopes bypass the cache
    const trySilent = async (): Promise<string> => {
        if (DEBUG_FORCE_INTERACTION_REQUIRED()) {
            throw new InteractionRequiredAuthError();
        }

        const silentRequest: SilentRequest = {
            scopes: scopesNeeded, // Required scopes for proper caching
            forceRefresh,
            ...(account && { account }), // Include account if available to use cached tokens
        };

        const tokenResponse = await instance.acquireTokenSilent(silentRequest);
        if (!tokenResponse.accessToken) {
            throw new Error("No token returned from MSAL");
        }
        return tokenResponse.accessToken;
    };

    try {
        return await trySilent();
    } catch (error) {
        // Another interactive flow holds MSAL's global interaction lock. Poll for
        // it to clear, retrying the silent acquisition (which then hits the cache).
        if (isInteractionInProgressError(error)) {
            for (let attempt = 0; attempt < INTERACTION_MAX_RETRIES; attempt++) {
                await wait(INTERACTION_RETRY_DELAY_MS);
                try {
                    return await trySilent();
                } catch (retryError) {
                    if (isInteractionInProgressError(retryError)) {
                        continue;
                    }
                    if (
                        retryError instanceof InteractionRequiredAuthError ||
                        retryError instanceof AuthError
                    ) {
                        return acquireTokenInteractive(instance, account);
                    }
                    throw retryError;
                }
            }
            // Lock never cleared within the retry window - likely stale.
            throw new Error("Authentication in progress, please wait");
        }

        if (error instanceof InteractionRequiredAuthError || error instanceof AuthError) {
            return acquireTokenInteractive(instance, account);
        }

        throw error;
    }
};

export const acquireAuthToken = async (
    instance: IPublicClientApplication,
    options: AcquireAuthTokenOptions = {},
): Promise<string | null> => {
    const forceRefresh = options.forceRefresh ?? false;
    const key = forceRefresh ? "force" : "silent";

    const existing =
        inFlightTokenRequests[key] ?? (key === "silent" ? inFlightTokenRequests.force : null);
    if (existing) {
        return existing;
    }

    const request = acquireAuthTokenInner(instance, forceRefresh);
    inFlightTokenRequests[key] = request;
    void request.finally(() => {
        if (inFlightTokenRequests[key] === request) {
            inFlightTokenRequests[key] = null;
        }
    });
    return request;
};

export const getAPI = async ({
    url,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method" | "payload">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "GET",
        };
        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

export const deleteAPI = async ({
    url,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method" | "payload">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "DELETE",
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

export const fetchWithAuth = async ({
    url,
    method,
    payload,
    instance,
    expectedStatus,
}: FetchParams): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method,
            body: payload ? JSON.stringify(payload) : undefined,
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

// Quick health check function
export const checkApiHealth = async (token?: string): Promise<boolean> => {
    // If we've detected a healthy state, trust it for the whole session
    if (healthCheckCache.isHealthy) {
        return true;
    }

    // For unhealthy state, check cache duration
    if (Date.now() - healthCheckCache.timestamp < CACHE_DURATION) {
        return false;
    }

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(BACKEND_URL, {
            method: "HEAD",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const isHealthy = response.ok;

        // Update cache
        healthCheckCache = {
            isHealthy,
            timestamp: Date.now(),
        };

        return isHealthy;
    } catch (error) {
        // Capture detailed error information for debugging
        Sentry.withScope((scope) => {
            scope.setTransactionName("checkApiHealth_internal");
            scope.setTag("error_type", "api_health_check_internal");
            scope.setExtra("backend_url", BACKEND_URL);
            scope.setExtra("error_name", error instanceof Error ? error.name : "Unknown");
            scope.setExtra("error_message", error instanceof Error ? error.message : String(error));
            scope.setExtra("has_token", !!token);
            scope.setExtra("cache_was_healthy", healthCheckCache.isHealthy);
            scope.setExtra("cache_age_ms", Date.now() - healthCheckCache.timestamp);

            // Don't capture AbortError (timeout) as critical - these are expected
            const level =
                error instanceof Error && error.name === "AbortError" ? "warning" : "error";

            Sentry.captureException(error, {
                level,
                tags: {
                    component: "checkApiHealth",
                    error_source:
                        error instanceof Error && error.name === "AbortError"
                            ? "timeout"
                            : "network_or_auth",
                },
            });
        });

        // Update cache with unhealthy state
        healthCheckCache = {
            isHealthy: false,
            timestamp: Date.now(),
        };
        return false;
    }
};
